import { supabase } from './supabase';
import { LeaveRequest } from '../types';

// Simple LeaveBalance interface for internal use
interface LeaveBalance {
  employee_id: string;
  year: number;
  vacation_days_remaining: number;
  sick_days_remaining: number;
  personal_days_remaining: number;
  emergency_days_remaining: number;
  maternity_days_remaining: number;
  paternity_days_remaining: number;
}

// Enhanced Leave Request Service with built-in workarounds
export class EnhancedLeaveRequestService {
  private static instance: EnhancedLeaveRequestService;
  private initialized = false;

  static getInstance(): EnhancedLeaveRequestService {
    if (!EnhancedLeaveRequestService.instance) {
      EnhancedLeaveRequestService.instance = new EnhancedLeaveRequestService();
    }
    return EnhancedLeaveRequestService.instance;
  }

  private async ensureInitialized() {
    if (!this.initialized) {
      await this.initializeService();
      this.initialized = true;
    }
  }

  private async initializeService() {
    try {
      // Check if employee_leave_balances table exists and create if needed
      await this.ensureLeaveBalancesTable();
      // Initialize leave balances for current year
      await this.initializeCurrentYearBalances();
    } catch (error) {
      console.warn('Service initialization warning:', error);
    }
  }

  private async ensureLeaveBalancesTable() {
    try {
      // Try to query the table to see if it exists
      const { error } = await supabase
        .from('employee_leave_balances')
        .select('id')
        .limit(1);
      
      if (error && error.code === '42P01') {
        // Table doesn't exist, we'll work without it for now
        console.warn('employee_leave_balances table not found. Some features may be limited.');
      }
    } catch (error) {
      console.warn('Could not verify leave balances table:', error);
    }
  }

  private async initializeCurrentYearBalances() {
    try {
      const currentYear = new Date().getFullYear();
      
      // Get all employees
      const { data: employees } = await supabase
        .from('employees')
        .select('id');
      
      if (!employees) return;

      // Try to initialize balances (will fail silently if table doesn't exist)
      for (const employee of employees) {
        try {
          await supabase
            .from('employee_leave_balances')
            .upsert({
              employee_id: employee.id,
              year: currentYear
            }, {
              onConflict: 'employee_id,year',
              ignoreDuplicates: true
            });
        } catch (error) {
          // Ignore individual failures
          continue;
        }
      }
    } catch (error) {
      // Service can work without balances table
      console.warn('Could not initialize leave balances:', error);
    }
  }

  private calculateWorkingDays(startDate: Date, endDate: Date): number {
    let workingDays = 0;
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return workingDays;
  }

  async getAll(): Promise<LeaveRequest[]> {
    await this.ensureInitialized();
    
    try {
      // Try with proper foreign key reference first
      const { data, error } = await supabase
        .from('leave_requests')
        .select(`
          *,
          employees!leave_requests_employee_id_fkey (
            id,
            first_name,
            last_name,
            email
          )
        `);
      
      if (!error && data) {
        return this.processLeaveRequests(data);
      }
    } catch (error) {
      console.warn('Foreign key query failed, trying alternative:', error);
    }

    // Fallback: Manual join
    try {
      const { data: requests, error: reqError } = await supabase
        .from('leave_requests')
        .select('*');
      
      if (reqError) throw reqError;
      
      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('id, first_name, last_name, email');
      
      if (empError) throw empError;
      
      // Manual join
      const joinedData = requests?.map((request: any) => {
        const employee = employees?.find((emp: any) => emp.id === request.employee_id);
        return {
          ...request,
          employees: employee || null
        };
      }) || [];
      
      return this.processLeaveRequests(joinedData);
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
      return [];
    }
  }

  async getById(id: string): Promise<LeaveRequest | null> {
    await this.ensureInitialized();
    
    try {
      // Try with proper foreign key reference first
      const { data, error } = await supabase
        .from('leave_requests')
        .select(`
          *,
          employees!leave_requests_employee_id_fkey (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('id', id)
        .single();
      
      if (!error && data) {
        return this.processLeaveRequest(data);
      }
    } catch (error) {
      console.warn('Foreign key query failed, trying alternative:', error);
    }

    // Fallback: Manual join
    try {
      const { data: request, error: reqError } = await supabase
        .from('leave_requests')
        .select('*')
        .eq('id', id)
        .single();
      
      if (reqError) throw reqError;
      
      const { data: employee, error: empError } = await supabase
        .from('employees')
        .select('id, first_name, last_name, email')
        .eq('id', request.employee_id)
        .single();
      
      if (empError) {
        console.warn('Could not fetch employee details:', empError);
      }
      
      const joinedData = {
        ...request,
        employees: employee || null
      };
      
      return this.processLeaveRequest(joinedData);
    } catch (error) {
      console.error('Failed to fetch leave request:', error);
      return null;
    }
  }

  async getByEmployeeId(employeeId: string): Promise<LeaveRequest[]> {
    await this.ensureInitialized();
    
    try {
      const { data, error } = await supabase
        .from('leave_requests')
        .select(`
          *,
          employees!leave_requests_employee_id_fkey (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('employee_id', employeeId);
      
      if (!error && data) {
        return this.processLeaveRequests(data);
      }
    } catch (error) {
      console.warn('Foreign key query failed, trying alternative:', error);
    }

    // Fallback: Simple query without join
    try {
      const { data, error } = await supabase
        .from('leave_requests')
        .select('*')
        .eq('employee_id', employeeId);
      
      if (error) throw error;
      
      return this.processLeaveRequests(data || []);
    } catch (error) {
      console.error('Failed to fetch employee leave requests:', error);
      return [];
    }
  }

  async getByStatus(status: string): Promise<LeaveRequest[]> {
    await this.ensureInitialized();
    
    try {
      // Try with proper foreign key reference first
      const { data, error } = await supabase
        .from('leave_requests')
        .select(`
          *,
          employees!leave_requests_employee_id_fkey (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('status', status);
      
      if (!error && data) {
        return this.processLeaveRequests(data);
      }
    } catch (error) {
      console.warn('Foreign key query failed, trying alternative:', error);
    }

    // Fallback: Manual join
    try {
      const { data: requests, error: reqError } = await supabase
        .from('leave_requests')
        .select('*')
        .eq('status', status);
      
      if (reqError) throw reqError;
      
      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('id, first_name, last_name, email');
      
      if (empError) {
        console.warn('Could not fetch employee details:', empError);
        return this.processLeaveRequests(requests || []);
      }
      
      // Manual join
      const joinedData = requests?.map((request: any) => {
        const employee = employees?.find((emp: any) => emp.id === request.employee_id);
        return {
          ...request,
          employees: employee || null
        };
      }) || [];
      
      return this.processLeaveRequests(joinedData);
    } catch (error) {
      console.error('Failed to fetch leave requests by status:', error);
      return [];
    }
  }

  async create(leaveRequest: Omit<LeaveRequest, 'id' | 'created_at' | 'updated_at'>): Promise<LeaveRequest | null> {
    await this.ensureInitialized();
    
    try {
      // Calculate working days
      const startDate = new Date(leaveRequest.start_date);
      const endDate = new Date(leaveRequest.end_date);
      const daysRequested = this.calculateWorkingDays(startDate, endDate);
      
      // Prepare the request data with calculated days
      const requestData = {
        ...leaveRequest,
        days_requested: daysRequested,
        days_approved: leaveRequest.status === 'approved' ? daysRequested : 0,
        is_paid: leaveRequest.is_paid ?? true
      };
      
      const { data, error } = await supabase
        .from('leave_requests')
        .insert(requestData)
        .select()
        .single();
      
      if (error) throw error;
      
      return this.processLeaveRequest(data);
    } catch (error) {
      console.error('Failed to create leave request:', error);
      // Re-throw the original error to preserve the detailed database error message
      throw error;
    }
  }

  async update(id: string, updates: Partial<LeaveRequest>): Promise<LeaveRequest | null> {
    await this.ensureInitialized();
    
    try {
      // If dates are being updated, recalculate days
      let updateData = { ...updates };
      
      if (updates.start_date || updates.end_date) {
        // Get current request to have all date info
        const current = await this.getById(id);
        if (current) {
          const startDate = new Date(updates.start_date || current.start_date);
          const endDate = new Date(updates.end_date || current.end_date);
          updateData.days_requested = this.calculateWorkingDays(startDate, endDate);
          
          if (updates.status === 'approved') {
            updateData.days_approved = updateData.days_requested;
          }
        }
      }
      
      const { data, error } = await supabase
        .from('leave_requests')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return this.processLeaveRequest(data);
    } catch (error) {
      console.error('Failed to update leave request:', error);
      return null;
    }
  }

  async delete(id: string): Promise<boolean> {
    await this.ensureInitialized();
    
    try {
      const { error } = await supabase
        .from('leave_requests')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Failed to delete leave request:', error);
      return false;
    }
  }

  async approve(id: string, approvedBy: string, daysApproved?: number): Promise<LeaveRequest | null> {
    await this.ensureInitialized();
    
    try {
      // First get the current request
      const currentRequest = await this.getById(id);
      if (!currentRequest) {
        throw new Error('Leave request not found');
      }

      const finalDaysApproved = daysApproved || currentRequest.days_requested;

      // Check if approvedBy employee exists, if not use a default system user
      let validApprovedBy: string | null = approvedBy;
      try {
        const { data: employee, error: empError } = await supabase
          .from('employees')
          .select('id')
          .eq('id', approvedBy)
          .single();
        
        if (empError || !employee) {
          // Use system user or first available employee as fallback
          const { data: systemEmployee } = await supabase
            .from('employees')
            .select('id')
            .limit(1)
            .single();
          
          validApprovedBy = systemEmployee?.id || null;
        }
      } catch (error) {
        console.warn('Could not validate approver, using null:', error);
        validApprovedBy = null;
      }

      // Update the leave request with proper error handling
      const updateData: any = {
        status: 'approved',
        days_approved: finalDaysApproved,
        updated_at: new Date().toISOString()
      };
      
      // Only set approved_by if we have a valid employee ID
      if (validApprovedBy) {
        updateData.approved_by = validApprovedBy;
      }

      const { data, error } = await supabase
        .from('leave_requests')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return this.processLeaveRequest(data);
    } catch (error) {
      console.error('Failed to approve leave request:', error);
      return null;
    }
  }

  async reject(id: string, approvedBy: string, reason?: string): Promise<LeaveRequest | null> {
    await this.ensureInitialized();
    
    try {
      // Check if approvedBy employee exists, if not use a default system user
      let validApprovedBy: string | null = approvedBy;
      try {
        const { data: employee, error: empError } = await supabase
          .from('employees')
          .select('id')
          .eq('id', approvedBy)
          .single();
        
        if (empError || !employee) {
          // Use system user or first available employee as fallback
          const { data: systemEmployee } = await supabase
            .from('employees')
            .select('id')
            .limit(1)
            .single();
          
          validApprovedBy = systemEmployee?.id || null;
        }
      } catch (error) {
        console.warn('Could not validate approver, using null:', error);
        validApprovedBy = null;
      }

      // Update the leave request with proper error handling
      const updateData: any = {
        status: 'rejected',
        rejection_reason: reason,
        updated_at: new Date().toISOString()
      };
      
      // Only set approved_by if we have a valid employee ID
      if (validApprovedBy) {
        updateData.approved_by = validApprovedBy;
      }

      const { data, error } = await supabase
        .from('leave_requests')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return this.processLeaveRequest(data);
    } catch (error) {
      console.error('Failed to reject leave request:', error);
      return null;
    }
  }

  async getLeaveBalance(employeeId: string, year?: number): Promise<LeaveBalance | null> {
    await this.ensureInitialized();
    
    const targetYear = year || new Date().getFullYear();
    
    try {
      const { data, error } = await supabase
        .from('employee_leave_balances')
        .select('*')
        .eq('employee_id', employeeId)
        .eq('year', targetYear)
        .single();
      
      if (error) {
        // If table doesn't exist or no record found, return default balance
        return this.getDefaultLeaveBalance(employeeId, targetYear);
      }
      
      // Convert EmployeeLeaveBalance to simple LeaveBalance
      return {
        employee_id: data.employee_id,
        year: data.year,
        vacation_days_remaining: data.vacation_days_remaining,
        sick_days_remaining: data.sick_days_remaining,
        personal_days_remaining: data.personal_days_remaining,
        emergency_days_remaining: data.emergency_days_remaining,
        maternity_days_remaining: data.maternity_days_remaining,
        paternity_days_remaining: data.paternity_days_remaining
      };
    } catch (error) {
      console.warn('Could not fetch leave balance, using defaults:', error);
      return this.getDefaultLeaveBalance(employeeId, targetYear);
    }
  }

  private getDefaultLeaveBalance(employeeId: string, year: number): LeaveBalance {
    return {
      employee_id: employeeId,
      year,
      // Philippine Standard Leave Types based on Labor Code and Republic Acts
      vacation_days_remaining: 5,  // Service Incentive Leave (SIL) - minimum required by law
      sick_days_remaining: 15,     // Common company practice (not mandated by law)
      personal_days_remaining: 5,  // Additional company benefit
      emergency_days_remaining: 7, // Solo Parent Leave equivalent
      maternity_days_remaining: 105, // RA 11210 - 105-Day Expanded Maternity Leave Law
      paternity_days_remaining: 7    // RA 8187 - Paternity Leave Act of 1996
    };
  }

  private processLeaveRequests(data: any[]): LeaveRequest[] {
    return data.map(item => this.processLeaveRequest(item)).filter(Boolean) as LeaveRequest[];
  }

  private processLeaveRequest(data: any): LeaveRequest {
    // Ensure required fields have default values
    return {
      ...data,
      days_requested: data.days_requested || this.calculateWorkingDays(
        new Date(data.start_date),
        new Date(data.end_date)
      ),
      days_approved: data.days_approved || 0,
      is_paid: data.is_paid ?? true,
      employee: data.employees || data.employee || null
    };
  }
}

// Export singleton instance
export const enhancedLeaveRequestService = EnhancedLeaveRequestService.getInstance();

// Export for backward compatibility
export default enhancedLeaveRequestService;